// src/Pages/ModuleRecords/LivestockAddRecord.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import RecordWizard from '../../component/reusable/formRecords/RecordWizards';
import { SelectWithOther } from '../../component/reusable/formRecords/formFieldPrimitives';
import { populationRecordsApi } from '../../api/populationRecordsApi';
import { sexCode } from '../../utils/populationSexGroups';
import { loadPopulationRefData } from '../../api/populationRefApi';
import { breedEcotypeApi, ageClassApi } from '../../api/speciesLivestockRegistryApi';
import { geospatialApi } from '../../api/geospatialApi';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useFormRegistrySchema } from '../../component/Admin/FoundationalTables/FormRegistry/formRegistryStore';
import { getRegistrySubmoduleOptions } from '../../component/Admin/FoundationalTables/FormRegistry/formRegistryData';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import {
  buildProductionPurposeOptions,
  getProductionPurposeLabel,
  resolveProductionPurposeValue,
} from '../../component/Livestock/Population/productionPurposeHelpers';
import {
  formatOtherOptionValue,
  splitOtherOptionValue,
} from '../../component/shared/forms/otherFieldHelpers';

const MODULE_NAME = 'Livestock Population';
const MODULE_KEY = 'livestock-population';

const SUB_MODULES = getRegistrySubmoduleOptions(MODULE_KEY).length > 0
  ? getRegistrySubmoduleOptions(MODULE_KEY)
  : [
  { value: 'population', label: 'Livestock Population Records' },
  ];

// ── DCIC-01 fallback ENUMs (used only when the backend registry returns nothing) ──
const PRODUCTION_SYSTEMS_FB = [
  { value: 'pastoral',       label: 'Pastoral' },
  { value: 'agropastoral',   label: 'Agropastoral' },
  { value: 'intensive',      label: 'Intensive' },
  { value: 'semi_intensive', label: 'Semi-Intensive' },
  { value: 'backyard',       label: 'Backyard' },
  { value: 'nomadic',        label: 'Nomadic' },
];

const COUNT_METHODS_FB = [
  { value: 'physical_count', label: 'Physical Count' },
  { value: 'naits_registry', label: 'NAITS Registry' },
  { value: 'lga_records',    label: 'LGA Records' },
  { value: 'estimate',       label: 'Estimate' },
  { value: 'aerial_survey',  label: 'Aerial Survey' },
];

const DATA_SOURCES_FB = [
  { value: 'fmld_field_count', label: 'FMLD Field Count' },
  { value: 'lga_report',       label: 'LGA Report' },
  { value: 'state_adp',        label: 'State ADP' },
  { value: 'nbs_survey',       label: 'NBS Survey' },
  { value: 'estimate',         label: 'Estimate' },
];

// DCIC-01 confidence_level: auto-derived from count_method, reduce-only at the
// backend — this form only ever displays it, it never submits the value.
function deriveConfidenceLevel(countMethod) {
  if (countMethod === 'physical_count') return 'High';
  if (countMethod === 'estimate') return 'Low';
  return 'Medium';
}

// Species code → production purposes (F8c, DCIC-01)
const PURPOSE_MAP = {
  cattle:             ['beef', 'dairy', 'breeding', 'draught', 'dual'],
  goat:               ['meat', 'dairy', 'breeding', 'dual'],
  sheep:              ['meat', 'wool', 'breeding', 'dual'],
  pig:                ['meat', 'festival_fattening'],
  broiler:            ['meat'],
  layer:              ['eggs', 'dual'],
  indigenous_chicken: ['eggs', 'meat', 'dual'],
  guinea_fowl:        ['meat', 'eggs', 'dual'],
  turkey:             ['meat', 'eggs'],
  rabbit:             ['meat'],
  honey_bee:          ['honey', 'pollination'],
  camel:              ['dairy', 'draught', 'meat'],
  horse:              ['draught', 'breeding'],
  donkey:             ['draught', 'breeding'],
};

const FALLBACK_PURPOSES = ['beef', 'dairy', 'meat', 'eggs', 'breeding', 'draught', 'dual', 'other'];

const PERIOD_YEARS = [2024, 2025, 2026, 2027];

function periodInstances(type) {
  switch (type) {
    case 'Baseline':  return PERIOD_YEARS.map((y) => `${y}-Baseline`);
    case 'Quarterly': return PERIOD_YEARS.flatMap((y) => ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${y}-${q}`));
    case 'Monthly':   return PERIOD_YEARS.flatMap((y) =>
      Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`)
    );
    case 'Annual':    return PERIOD_YEARS.map(String);
    default:          return [];
  }
}


function purposesFor(speciesCode) {
  if (!speciesCode) return FALLBACK_PURPOSES;
  const key = speciesCode.toLowerCase().replace(/[\s-]+/g, '_');
  return PURPOSE_MAP[key]
    || PURPOSE_MAP[Object.keys(PURPOSE_MAP).find((k) => key.includes(k))]
    || FALLBACK_PURPOSES;
}

const lbl = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
// Head counts as the Livestock Population table shows them (1,250).
const formatCount = (value) => (value === '' || value === null || value === undefined || Number.isNaN(Number(value))
  ? value
  : Number(value).toLocaleString('en-NG'));

// ── Edit-mode helpers (ported from PopulationEditModal.jsx) ───────────────────
function guessPeriodType(s) {
  if (!s) return '';
  if (/baseline/i.test(s))  return 'Baseline';
  if (/-Q\d$/.test(s))      return 'Quarterly';
  if (/-\d{2}$/.test(s))    return 'Monthly';
  if (/^\d{4}$/.test(s))    return 'Annual';
  return 'Quarterly';
}

// Role → capability matrix, used to gate pushWorkflowNotification the same
// way the Population table's row-action menu does.
const ROLE_CAN = {
  R01: { edit: true, submit: true,  validate: true,  approve: true,  reject: true,  delete: true  },
  R02: { edit: false, submit: false, validate: false, approve: true,  reject: true,  delete: false },
  R03: { edit: false, submit: false, validate: true,  approve: false, reject: true,  delete: false },
  R04: { edit: true, submit: true,  validate: true,  approve: false, reject: true,  delete: false },
  R05: { edit: true, submit: true,  validate: false, approve: false, reject: false, delete: false },
};

// Role → { currentStatus: [allowed next statuses] } for the senior-role
// "Record Status" transition dropdown in edit mode.
const ROLE_TRANSITIONS = {
  R01: { draft: ['submitted'], submitted: ['validated', 'rejected'], validated: ['approved', 'rejected'], approved: ['rejected'], rejected: ['submitted'] },
  R02: { validated: ['approved', 'rejected'] },
  R03: { draft: [],            submitted: ['validated', 'rejected'], validated: ['rejected'],             approved: [],           rejected: [] },
  R04: { draft: ['submitted'], submitted: ['validated', 'rejected'], validated: ['rejected'],             approved: [],           rejected: [] },
};

// Role → which record statuses that role may edit the CONTENT fields of.
// Mirrors the live backend's authoritative rule, PopulationRecordPolicy::update()
// (NlimsBackend/app/Policies/PopulationRecordPolicy.php) — NOT a frontend guess:
// the status gate (Draft or Rejected only) is identical for every permitted
// role, and only SystemAdministrator (R01), SubNationalValidator (R04), and
// DataOriginator (R05) are in the allowed-role list at all. R02/R03 (the
// national data-steward/reviewer roles) cannot edit content via this endpoint
// under any status — they only act through the separate submit/validate/
// approve/reject transition endpoints.
const EDITABLE_STATUSES_BY_ROLE = {
  R01: ['draft', 'rejected'],
  R02: [],
  R03: [],
  R04: ['draft', 'rejected'],
  R05: ['draft', 'rejected'],
};

function recordLabel(row) {
  return `${row?.state ?? ''}${row?.lga ? ` — ${row.lga}` : ''}`.trim() || row?.uuid || row?.id;
}

const FALLBACK_AGE_CLASSES = [
  { value: 'calf_kid_lamb',   label: 'Calf / Kid / Lamb  (< 1 yr)' },
  { value: 'juvenile',        label: 'Juvenile  (1–2 yrs)' },
  { value: 'adult',           label: 'Adult  (> 2 yrs)' },
  { value: 'breeding_female', label: 'Breeding Female' },
  { value: 'breeding_male',   label: 'Breeding Male' },
  { value: 'mixed',           label: 'Mixed (all classes)' },
];

const normalizeNumberInput = (value) => String(value ?? '').replace(/[^\d]/g, '');

function getSpeciesUuid(species) {
  return species?.uuid ?? species?.id ?? species?.species_id ?? species?.speciesUuid ?? '';
}
function getSpeciesCode(species) {
  return species?.species_code ?? species?.speciesCode ?? species?.code ?? '';
}

// ── Primitive UI helpers (shared visual language across sub-modules) ─────────
// Flat, borderless, light-gray fill — matches the Add New Record mockups.
const INP =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] ' +
  'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white appearance-none';
const INP_LOCKED =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-400 bg-gray-50 cursor-not-allowed appearance-none';
const LBL = 'block text-[13px] font-bold text-gray-900 mb-1.5';

function Field({ label: labelText, required, hint, children }) {
  return (
    <div>
      <label className={LBL}>
        {labelText}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

// Native <select> wrapped with a custom chevron (appearance-none hides the
// browser's own arrow so the flat mockup styling renders consistently).
function Select({ value, onChange, disabled, className = INP, children }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`${className} pr-9`}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
      >
        {children}
      </select>
      <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function SectionHead({ title }) {
  return (
    <span className="block text-xs font-bold uppercase tracking-wider text-gray-500">
      {title}
    </span>
  );
}

// Thin green rule that separates one field-group section from the next.
function SectionDivider() {
  return <div className="h-px w-full bg-green-200" />;
}

function ReadonlyBox({ children }) {
  return (
    <div className="flex items-center min-h-[2.75rem] px-4 py-3 rounded-lg bg-gray-50 text-xs text-gray-400 italic">
      {children}
    </div>
  );
}

const WIZARD_STEPS = [
  { key: 'basic',  label: 'Basic Data' },
  { key: 'animal', label: 'Animal Information Data' },
  { key: 'system', label: 'System Info' },
  { key: 'review', label: 'Review' },
];

// Sex checkboxes: Male and Female each take their own count (one record per
// sex); Combined means one count covering both sexes, so it excludes the
// other two.
const SEX_CHOICES = [
  { key: 'male', label: 'Male' },
  { key: 'female', label: 'Female' },
  { key: 'combined', label: 'Combined' },
];
const EMPTY_POP = {
  state: '', lga: '', ward: '',
  period_type: '', period_instance: '',
  species_uuid: '', species_code: '',
  production_system: '', production_purpose: '',
  // Superseded by the per-sex counts below (sexRows) in create and edit.
  population_count: '',
  sex: 'male',
  // C1 long-format (create mode): the officer enters a count PER SEX and the
  // system writes one record per sex, unless the split truly isn't known, in
  // which case a single row is written with sex = 'combined'.
  sex_male: false,
  sex_female: false,
  sex_combined: false,
  male_count: '',
  female_count: '',
  combined_count: '',
  age_class: '',
  number_herds_flocks: '',
  period_offtake: '',
  count_method: '',
  data_source: '',
  data_source_other: '',
  breed_uuid: '',
  notes: '',
};

const TAB_ROUTE = {
  population: '/livestock-population/livestock-population-record',
};

export default function LivestockAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const isEditMode = Boolean(uuid);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshNotifications, pushWorkflowNotification } = useNotifications();
  const roleId = user?.roleId ?? 'R06';
  const isR04 = roleId === 'R04';
  const isR05 = roleId === 'R05';
  const isFieldUser = isR04 || isR05;
  // Age Group / Breed-Ecotype visibility: create mode only restricts R05
  // (matches CreatePopulationModal); edit mode restricts both R04 and R05
  // (matches PopulationEditModal) — each mode keeps its original behavior.
  const hideRestrictedFields = isEditMode ? isFieldUser : isR05;

  const initialSubModule = SUB_MODULES.some((s) => s.value === searchParams.get('subModule'))
    ? searchParams.get('subModule')
    : 'population';
  const [subModule, setSubModuleState] = useState(isEditMode ? 'population' : initialSubModule);
  const setSubModule = setSubModuleState;
  void setSubModule;
  const { schema: registrySchema } = useFormRegistrySchema(MODULE_KEY, subModule);
  const schemaSections = useMemo(() => registrySchema?.sections ?? [], [registrySchema]);
  const schemaSectionsById = useMemo(
    () => new Map(schemaSections.map((section) => [String(section.id ?? '').trim(), section])),
    [schemaSections]
  );
  const schemaFieldsBySectionId = useMemo(
    () => new Map(schemaSections.map((section) => [String(section.id ?? '').trim(), Array.isArray(section.fields) ? section.fields : []])),
    [schemaSections]
  );
  const moduleScopeLabel = registrySchema?.moduleName || MODULE_NAME;
  const subModuleScopeLabel = registrySchema?.submoduleName || SUB_MODULES.find((option) => option.value === subModule)?.label || 'Livestock Population Records';

  const [step, setStep] = useState(0);

  // ── Edit-mode state ─────────────────────────────────────────────────────
  const [editRow, setEditRow] = useState(null);
  // The record(s) being edited/viewed — the one record opened from the table.
  // Ticking another sex on edit adds a record for it (C1: one per sex).
  const [editMembers, setEditMembers] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(isEditMode);
  const [statusSelect, setStatusSelect] = useState('draft');
  const [rejectionNotes, setRejectionNotes] = useState('');

  // ── Population form state ──────────────────────────────────────────────
  const [popForm, setPopForm] = useState({
    ...EMPTY_POP,
    state: (isR04 || isR05) ? (user?.assignedState ?? '') : '',
    lga:   isR05 ? (user?.assignedLga   ?? '') : '',
    ward:  isR05 ? (user?.assignedWard  ?? '') : '',
  });
  const [lgasList, setLgasList]   = useState([]);
  const [wardsList, setWardsList] = useState([]);
  const [breedList, setBreeds]    = useState([]);
  const [allAgeClasses, setAllAgeClasses] = useState([]);
  const [loadingLgas, setLoadingLgas]   = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  const [loadingBreeds, setLoadingBreeds] = useState(false);
  const [rawCountMethods, setRawCountMethods] = useState([]);

  // ── Ref data ────────────────────────────────────────────────────────────
  const [popRefData, setPopRefData] = useState({});
  const [refLoading, setRefLoading] = useState(true);

  // ── Workflow / submit state ────────────────────────────────────────────
  const [formStatus, setFormStatus]   = useState('draft');
  // One saved draft per sex row (1 in combined mode, up to 2 in split mode).
  const [savedRecords, setSavedRecords] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  // ── Load Population reference data (states, species, prod systems, etc.) ──
  useEffect(() => {
    loadPopulationRefData()
      .then((data) => setPopRefData(data || {}))
      .catch(() => setPopRefData({}))
      .finally(() => setRefLoading(false));
  }, []);

  // ── Count methods — fetched directly so R05 always sends an accepted value ──
  useEffect(() => {
    client.get('/v1/count-methods', { params: { per_page: 200 } })
      .then(({ data }) => {
        const payload = data?.data ?? data ?? {};
        const items = Array.isArray(payload) ? payload
          : Array.isArray(payload.items) ? payload.items
          : Array.isArray(payload.results) ? payload.results
          : [];
        setRawCountMethods(items.length > 0
          ? items.map((i) => ({ value: i.code ?? i.value ?? i.name ?? i.uuid, label: i.name ?? i.label ?? i.code ?? i.uuid }))
          : []);
      })
      .catch(() => setRawCountMethods([]));
  }, []);

  useEffect(() => {
    if (!isR05 || isEditMode) return;
    const opts = rawCountMethods.length > 0 ? rawCountMethods : COUNT_METHODS_FB;
    const best = opts.find((m) => m.value === 'physical_count')?.value || opts[0]?.value || '';
    setPopForm((prev) => ({ ...prev, count_method: best }));
  }, [rawCountMethods, isR05, isEditMode]);

  // ── Edit/View mode: load the existing record ────────────────────────────
  // Prefer the already-normalized row handed over via navigation state (the
  // exact object the table/list already fetched from the live API) — this
  // is known-complete. Fall back to a fresh live GET only when the page is
  // opened without that state (e.g. a direct link or a refresh).
  useEffect(() => {
    if (!isEditMode) return;
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setEditRow(seedRow);
      setEditMembers([seedRow]);
      setLoadingRecord(false);
      return;
    }
    setLoadingRecord(true);
    populationRecordsApi.get(uuid)
      .then((row) => {
        setEditRow(row);
        setEditMembers([row]);
      })
      .catch((e) => setError(e?.response?.data?.message ?? e.message ?? 'Failed to load record.'))
      .finally(() => setLoadingRecord(false));
  }, [isEditMode, uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ageClassApi.listAll({ per_page: 200 })
      .then(({ items }) => { if (items.length > 0) setAllAgeClasses(items); })
      .catch(() => {});
  }, []);

  const ageClassOpts = useMemo(() => {
    if (allAgeClasses.length === 0) return FALLBACK_AGE_CLASSES;
    const filtered = popForm.species_uuid
      ? allAgeClasses.filter((a) => a.species_uuid === popForm.species_uuid)
      : allAgeClasses;
    const sorted = [...filtered].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
    return sorted.length > 0
      ? sorted.map((a) => ({ value: a.age_class_code, label: a.age_class_label || a.age_class_code }))
      : FALLBACK_AGE_CLASSES;
  }, [allAgeClasses, popForm.species_uuid]);

  useEffect(() => {
    if (!popForm.state || isR05) return;
    setLoadingLgas(true);
    geospatialApi.lgas(popForm.state)
      .then((list) => setLgasList(Array.isArray(list) ? list.filter(Boolean) : []))
      .catch(() => setLgasList([]))
      .finally(() => setLoadingLgas(false));
  }, [popForm.state, isR05]);

  useEffect(() => {
    if (!popForm.state || !popForm.lga) { setWardsList([]); return; }
    setLoadingWards(true);
    geospatialApi.wards(popForm.state, popForm.lga)
      .then((list) => {
        const names = (Array.isArray(list) ? list : [])
          .map((w) => (typeof w === 'string' ? w : (w?.ward ?? w?.name ?? '')))
          .filter(Boolean);
        setWardsList(names);
      })
      .catch(() => setWardsList([]))
      .finally(() => setLoadingWards(false));
  }, [popForm.state, popForm.lga]);

  useEffect(() => {
    if (!popForm.species_uuid) { setBreeds([]); return; }
    setLoadingBreeds(true);
    breedEcotypeApi.listAll({ species_uuid: popForm.species_uuid, per_page: 200 })
      .then(({ items }) => setBreeds(Array.isArray(items) ? items : []))
      .catch(() => setBreeds([]))
      .finally(() => setLoadingBreeds(false));
  }, [popForm.species_uuid]);

  const {
    species = [],
    states = [],
    productionSystems: apiProdSys = [],
    dataSources: apiDataSources = [],
    productionPurposes: apiProductionPurposes = [],
  } = popRefData;
  const productionSystems = apiProdSys.length > 0 ? apiProdSys : PRODUCTION_SYSTEMS_FB;
  const dataSources       = apiDataSources.length > 0 ? apiDataSources : DATA_SOURCES_FB;
  const countMethods      = rawCountMethods.length > 0 ? rawCountMethods : COUNT_METHODS_FB;
  const purposes          = useMemo(() => {
    const registryOptions = buildProductionPurposeOptions({
      speciesUuid: popForm.species_uuid,
      speciesList: species,
      productionPurposes: apiProductionPurposes,
    });
    if (registryOptions.length > 0) return registryOptions;
    return purposesFor(popForm.species_code).map((purpose) => ({ value: purpose, label: lbl(purpose) }));
  }, [apiProductionPurposes, popForm.species_code, popForm.species_uuid, species]);

  // ── Edit mode: pre-populate the form once the record has loaded ────────
  useEffect(() => {
    if (!isEditMode || !editRow) return;
    const periodType = guessPeriodType(editRow.period);
    const speciesObj = species.find((s) => s.uuid === editRow.speciesUuid);
    const { value: dataSourceValue, otherValue: dataSourceOther } = splitOtherOptionValue(
      editRow.dataSource ?? editRow.data_source ?? '',
      dataSources
    );
    // Sex checkboxes + counts, exactly as Create New captures them.
    const members = editMembers ?? [editRow];
    const countOf = (code) => {
      const member = members.find((m) => sexCode(m) === code);
      return member ? String(member.populationCount ?? '') : '';
    };
    const combinedMember = members.find((m) => sexCode(m) === 'combined');
    setPopForm({
      sex_combined:        Boolean(combinedMember),
      sex_male:            !combinedMember && members.some((m) => sexCode(m) === 'male'),
      sex_female:          !combinedMember && members.some((m) => sexCode(m) === 'female'),
      male_count:          combinedMember ? '' : countOf('male'),
      female_count:        combinedMember ? '' : countOf('female'),
      combined_count:      combinedMember ? String(combinedMember.populationCount ?? '') : '',
      state:               editRow.state ?? '',
      lga:                 editRow.lga ?? '',
      ward:                editRow.ward ?? '',
      period_type:         periodType,
      period_instance:     editRow.period ?? '',
      species_uuid:        editRow.speciesUuid ?? '',
      species_code:        speciesObj?.species_code ?? '',
      production_system:   editRow.productionSystem?.toLowerCase().replace(/\s/g, '_') ?? '',
      production_purpose:  editRow.productionPurposeUuid ?? editRow.production_purpose_uuid ?? editRow.productionPurposeCode ?? editRow.production_purpose?.uuid ?? editRow.production_purpose?.code ?? '',
      age_class:           editRow.ageClassCode ?? '',
      number_herds_flocks: editRow.herdsFlocksCount ?? editRow.numberHerdsFlocks ?? editRow.herds_flocks_count ?? editRow.number_herds_flocks ?? null
        ? String(editRow.herdsFlocksCount ?? editRow.numberHerdsFlocks ?? editRow.herds_flocks_count ?? editRow.number_herds_flocks)
        : '',
      period_offtake:      editRow.periodOfftake != null ? String(editRow.periodOfftake) : '',
      count_method:        editRow.countMethodCode ?? '',
      data_source:         dataSourceValue,
      data_source_other:   dataSourceOther,
      breed_uuid:          editRow.breedUuid ?? '',
      notes:               editRow.notes ?? '',
    });
    setStatusSelect((editRow.data_status ?? 'draft').toLowerCase());
  }, [isEditMode, editRow, editMembers, dataSources]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync species_code when reference data arrives after the form was pre-populated.
  useEffect(() => {
    if (!isEditMode || !popForm.species_uuid || popForm.species_code || species.length === 0) return;
    const obj = species.find((s) => s.uuid === popForm.species_uuid);
    if (obj?.species_code) setPopForm((prev) => ({ ...prev, species_code: obj.species_code }));
  }, [species, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStatus  = (editRow?.data_status ?? 'draft').toLowerCase();
  // Content-field editability follows the per-role/status matrix above —
  // R01 can edit at every stage, R02/R03 while Submitted or Validated, R04
  // only once Submitted, R05 only while Draft or Rejected. Everything else
  // stays read-only and only the Record Status transition can be applied.
  const canEditFields  = (EDITABLE_STATUSES_BY_ROLE[roleId] ?? []).includes(currentStatus);
  const fieldsLocked   = isEditMode && !canEditFields;
  const isRejectingEdit  = isEditMode && !isFieldUser && statusSelect === 'rejected';
  const statusChangedEdit = isEditMode && !isFieldUser && statusSelect !== currentStatus;

  // Same edit-permission matrix as the table's row-action menu (getMenuItems
  // in PopulationDataTable.jsx) — used to decide whether the View page shows
  // an "Edit Record" button for the current viewer.
  const canEditThisRecord = canEditFields;

  // ── Controlled field helper (population) ───────────────────────────────
  const setPop = (key) => (e) =>
    {
      const value = e?.target ? e.target.value : e;
    setPopForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'state'        ? { lga: '', ward: '' }   : {}),
      ...(key === 'lga'          ? { ward: '' }             : {}),
      ...(key === 'period_type'  ? { period_instance: '' }  : {}),
      ...(key === 'data_source' && String(value).toLowerCase() !== 'other'
        ? { data_source_other: '' }
        : {}),
      ...(key === 'species_uuid' ? {
        breed_uuid:         '',
        production_purpose: '',
        age_class:          '',
        species_code: species.find((s) => s.uuid === value)?.species_code ?? '',
      } : {}),
    }));
    };

  // ── C1 sex rows (create mode) ───────────────────────────────────────────
  // One { sex, population_count } per row the system will write: a single
  // 'combined' row, or a male and/or female row for each count > 0.
  const sexRows = popForm.sex_combined
    ? [{ sex: 'combined', population_count: Number(popForm.combined_count) }]
    : [
      ...(popForm.sex_male ? [{ sex: 'male', population_count: Number(popForm.male_count) }] : []),
      ...(popForm.sex_female ? [{ sex: 'female', population_count: Number(popForm.female_count) }] : []),
    ];

  // At least one sex ticked, and every ticked sex has a count ≥ 1.
  const sexCountsValid = sexRows.length > 0 && sexRows.every((row) => row.population_count >= 1);

  const sexTotal = sexRows.reduce((sum, row) => sum + row.population_count, 0);

  // ── Step validation ─────────────────────────────────────────────────────
  const basicValid = Boolean(popForm.state) && Boolean(popForm.lga) && Boolean(popForm.period_type) && Boolean(popForm.period_instance);

  const animalValid = Boolean(popForm.species_uuid) && Boolean(popForm.production_system) && Boolean(popForm.production_purpose) &&
    sexCountsValid &&
    (hideRestrictedFields || Boolean(popForm.age_class)) &&
    Number(popForm.number_herds_flocks) >= 1 && popForm.period_offtake !== '';

  const systemValid = Boolean(popForm.count_method) && Boolean(popForm.data_source) &&
    (String(popForm.data_source).toLowerCase() !== 'other' || Boolean(popForm.data_source_other.trim()));

  // When the record's fields are locked (e.g. Validated/Submitted, viewed by
  // a reviewer), don't gate step navigation on field validity — the point of
  // moving forward is to reach the Record Status control, not to re-validate
  // content the user can't change.
  const stepValid = fieldsLocked ? true : [basicValid, animalValid, systemValid, true][step];

  const goToTab = useCallback(() => navigate(TAB_ROUTE[subModule] ?? TAB_ROUTE.population), [navigate, subModule]);

  // ── Population payload / actions (ported from CreatePopulationModal) ──
  // C1 long-format: one payload per sex row (see sexRows above).
  const buildPopPayloads = (status) => {
    const common = buildPopPayload(status);
    return sexRows.map((row) => ({ ...common, sex: row.sex, population_count: row.population_count }));
  };

  const buildPopPayload = (status) => ({
    state:              popForm.state,
    lga:                popForm.lga,
    ward:               popForm.ward || null,
    reference_period:   popForm.period_instance || null,
    species_uuid:       popForm.species_uuid,
    production_system:  popForm.production_system,
    production_purpose_label: getProductionPurposeLabel(popForm.production_purpose, purposes),
    production_purpose_uuid: resolveProductionPurposeValue(popForm.production_purpose, {
      speciesUuid: popForm.species_uuid,
      speciesList: species,
      productionPurposes: apiProductionPurposes,
    }) || null,
    population_count:   Number(popForm.population_count),
    sex:                popForm.sex,
    age_class:          (!isR05 && popForm.age_class) ? popForm.age_class : null,
    herds_flocks_count: Number(popForm.number_herds_flocks) || null,
    period_offtake:     popForm.period_offtake !== '' ? Number(popForm.period_offtake) : null,
    count_method:       popForm.count_method || null,
    data_source:        formatOtherOptionValue(popForm.data_source, popForm.data_source_other, dataSources) || null,
    breed_uuid:         (!isR05 && popForm.breed_uuid) ? popForm.breed_uuid : null,
    data_status:        status,
  });

  // ── Edit payload / actions (ported from PopulationEditModal) ────────────
  const buildEditPayload = () => ({
    state:              popForm.state,
    lga:                popForm.lga,
    ward:               popForm.ward || null,
    reference_period:   popForm.period_instance || null,
    species_uuid:       popForm.species_uuid,
    production_system:  popForm.production_system,
    production_purpose_label: getProductionPurposeLabel(popForm.production_purpose, purposes),
    production_purpose_uuid: resolveProductionPurposeValue(popForm.production_purpose, {
      speciesUuid: popForm.species_uuid,
      speciesList: species,
      productionPurposes: apiProductionPurposes,
    }) || null,
    age_class:          (!isFieldUser && popForm.age_class) ? popForm.age_class : null,
    herds_flocks_count: popForm.number_herds_flocks !== '' ? Number(popForm.number_herds_flocks) : null,
    period_offtake:     popForm.period_offtake !== '' ? Number(popForm.period_offtake) : null,
    count_method:       popForm.count_method || null,
    data_source:        formatOtherOptionValue(popForm.data_source, popForm.data_source_other, dataSources) || null,
    breed_uuid:         (!isFieldUser && popForm.breed_uuid) ? popForm.breed_uuid : null,
    notes:              isR05 ? (editRow?.notes || null) : (popForm.notes || null),
  });

  const applyEditStatusAction = async (recUuid, newStatus, notes) => {
    switch (newStatus) {
      case 'submitted': await populationRecordsApi.submit(recUuid);              break;
      case 'validated': await populationRecordsApi.validate(recUuid);           break;
      case 'approved':  await populationRecordsApi.approve(recUuid);            break;
      case 'rejected':  await populationRecordsApi.reject(recUuid, notes ?? ''); break;
      default: break;
    }
  };

  // Saves the way Create New writes records: one per ticked sex. A ticked sex
  // that already has a record is updated, a newly ticked sex gets a new
  // record, and an unticked sex has its record deleted — which the backend
  // only allows R01 on a Draft. Status changes apply to every record saved.
  const handleEditSave = async ({ submitAfterSave = false } = {}) => {
    if (!uuid || saving) return;
    setSaving(true); setError('');
    try {
      const members = editMembers ?? (editRow ? [editRow] : []);
      let recordUuids = members.map((m) => m.uuid);
      const body = buildEditPayload();

      if (canEditFields) {
        const bySex = new Map(members.map((m) => [sexCode(m), m]));
        const keep = new Set(sexRows.map((row) => row.sex));
        const removed = members.filter((m) => !keep.has(sexCode(m)));
        if (removed.length && !(roleId === 'R01' && currentStatus === 'draft')) {
          throw new Error(
            `The ${removed.map((m) => lbl(sexCode(m))).join(' & ')} record cannot be removed — only an Administrator (R01) can delete a Draft record. Keep it ticked.`
          );
        }
        recordUuids = await Promise.all(sexRows.map(async (row) => {
          const existing = bySex.get(row.sex);
          if (existing) {
            await populationRecordsApi.update(existing.uuid, { ...body, sex: row.sex, population_count: row.population_count });
            return existing.uuid;
          }
          const created = await populationRecordsApi.create({ ...buildPopPayload('draft'), sex: row.sex, population_count: row.population_count });
          return created?.uuid;
        }));
        recordUuids = recordUuids.filter(Boolean);
        await Promise.all(removed.map((m) => populationRecordsApi.remove(m.uuid)));
      }

      const forEachRecord = (fn) => Promise.all(recordUuids.map(fn));
      if (submitAfterSave) {
        await forEachRecord((id) => populationRecordsApi.submit(id));
        pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: 'submitted', actorRoleId: roleId, recordLabel: recordLabel(body) });
      } else if (!isFieldUser && statusSelect === 'draft' && currentStatus !== 'draft') {
        await forEachRecord((id) => populationRecordsApi.resetToDraft(id));
      } else if (!isFieldUser && statusSelect && statusSelect !== currentStatus) {
        await forEachRecord((id) => applyEditStatusAction(id, statusSelect, rejectionNotes));
        pushWorkflowNotification({ moduleKey: 'population', roleCan: ROLE_CAN, newStatus: statusSelect, actorRoleId: roleId, recordLabel: recordLabel(body), notes: rejectionNotes });
      }
      refreshNotifications();
      goToTab();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Save failed.');
      setSaving(false);
    }
  };

  // For field-locked records: only a status change (+ notes if rejecting) is required.
  // For editable records: all required fields must be valid.
  const canSubmitEdit = fieldsLocked
    ? statusChangedEdit && (!isRejectingEdit || rejectionNotes.trim().length > 0)
    : Boolean(popForm.state) && Boolean(popForm.lga) && Boolean(popForm.period_instance) &&
      Boolean(popForm.species_uuid) && Boolean(popForm.production_system) &&
      Boolean(popForm.production_purpose) && sexCountsValid &&
      Boolean(popForm.data_source) &&
      (String(popForm.data_source).toLowerCase() !== 'other' || Boolean(popForm.data_source_other.trim())) &&
      (!isRejectingEdit || rejectionNotes.trim().length > 0);

  const handlePopSaveDraft = async () => {
    if (saving) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payloads = buildPopPayloads('draft');
      // Re-saving updates the draft already written for the same sex; a sex
      // row that no longer has a count (e.g. switched to Combined) is removed
      // so no stale draft is left behind.
      const records = await Promise.all(payloads.map((payload) => {
        const existing = savedRecords.find((r) => r.sex === payload.sex);
        return existing
          ? populationRecordsApi.update(existing.uuid, payload)
          : populationRecordsApi.create(payload);
      }));
      const keptSexes = new Set(payloads.map((p) => p.sex));
      await Promise.all(savedRecords
        .filter((r) => !keptSexes.has(r.sex))
        .map((r) => populationRecordsApi.remove(r.uuid)));
      setSavedRecords(records.map((record, i) => ({ uuid: record?.uuid, sex: payloads[i].sex })));
      setFormStatus('draft');
      setSuccessMsg(records.length > 1 ? 'Draft saved — one record per sex.' : 'Draft saved successfully.');
      refreshNotifications();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handlePopCreate = async () => {
    if (saving) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      await Promise.all(buildPopPayloads('draft').map((payload) => populationRecordsApi.create(payload)));
      refreshNotifications();
      goToTab();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Create failed.');
      setSaving(false);
    }
  };

  const handlePopSubmitForReview = async () => {
    if (savedRecords.length === 0 || submitting) return;
    setShowSubmitConfirm(false);
    setSubmitting(true); setError(''); setSuccessMsg('');
    try {
      await Promise.all(savedRecords.map((r) => populationRecordsApi.submit(r.uuid)));
      refreshNotifications();
      goToTab();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Submit failed.');
      setSubmitting(false);
    }
  };

  const statusLabel = isEditMode ? currentStatus : formStatus;

  const getSchemaSection = (sectionId, fallbackTitle) => schemaSectionsById.get(sectionId) ?? {
    id: sectionId,
    title: fallbackTitle,
    fields: [],
  };

  const getSchemaFields = (sectionId) => schemaFieldsBySectionId.get(sectionId) ?? [];

  const normalizeInlineOptions = (items, { lowercaseValues = false } = {}) => (Array.isArray(items) ? items : [])
    .map((item, index) => {
      if (item === null || item === undefined) return null;

      if (typeof item === 'string' || typeof item === 'number') {
        const text = String(item).trim();
        if (!text) return null;
        return {
          value: lowercaseValues ? text.toLowerCase().replace(/\s+/g, '_') : text,
          label: text,
        };
      }

      if (typeof item !== 'object') return null;

      const value = String(item.value ?? item.id ?? item.code ?? item.label ?? item.name ?? `option-${index + 1}`).trim();
      if (!value) return null;

      return {
        value,
        label: String(item.label ?? item.name ?? value).trim() || value,
      };
    })
    .filter(Boolean);

  const getFieldOptions = (field) => {
    switch (field?.id) {
      case 'period-type':
        return normalizeInlineOptions(['Baseline', 'Monthly', 'Quarterly', 'Annual']);
      case 'period-instance':
        return normalizeInlineOptions(periodInstances(popForm.period_type));
      case 'livestock-species':
        return normalizeInlineOptions(species.map((item) => ({
          value: item.uuid,
          label: item.common_name_nigeria || item.species_code || item.uuid,
        })));
      case 'production-system':
        return normalizeInlineOptions(productionSystems);
      case 'production-purpose':
        return normalizeInlineOptions(purposes);
      case 'count-method':
        return normalizeInlineOptions(countMethods);
      case 'data-source':
        return normalizeInlineOptions(dataSources);
      case 'age-group':
        return normalizeInlineOptions(ageClassOpts);
      case 'breed-ecotype':
        return normalizeInlineOptions(breedList.map((item) => ({
          value: item.uuid,
          label: item.breed_name || item.breed_code || item.uuid,
        })));
      default:
        return normalizeInlineOptions(field?.options);
    }
  };

  const renderFieldShell = (field, children) => (
    <div className={field?.layout === 'full-width' ? 'col-span-2' : 'col-span-1'}>
      <Field label={field?.label} required={Boolean(field?.required)} hint={field?.hint}>
        {children}
      </Field>
    </div>
  );

  const renderSelectField = (field, value, onChange, options, { disabled = false, placeholder } = {}) => renderFieldShell(
    field,
    <Select value={value} onChange={onChange} disabled={disabled}>
      <option value="">{placeholder ?? field?.placeholder ?? 'Select...'}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );

  const renderInputField = (field, value, onChange, { disabled = false, type = 'text', placeholder, min, max, step: inputStep } = {}) => renderFieldShell(
    field,
    <input
      type={type}
      min={min}
      max={max}
      step={inputStep}
      value={value}
      onChange={onChange}
      placeholder={placeholder ?? field?.placeholder ?? (type === 'number' ? '0' : 'Enter text')}
      disabled={disabled}
      className={INP}
    />
  );

  const renderTextareaField = (field, value, onChange, { disabled = false, rows = 3, placeholder } = {}) => renderFieldShell(
    field,
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder ?? field?.placeholder ?? 'Enter details'}
      disabled={disabled}
      className="w-full rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white resize-none placeholder-gray-400 disabled:cursor-not-allowed disabled:text-gray-400"
    />
  );

  // C1 long-format sex capture (create mode): male + female counts, or one
  // combined count when the split isn't known. Each count > 0 becomes its
  // own record on save.
  // Ticking Combined clears Male/Female (and the reverse) — Combined is one
  // count for both sexes.
  const toggleSex = (key) => (event) => {
    const checked = event.target.checked;
    setPopForm((prev) => {
      if (key === 'combined') {
        return checked
          ? { ...prev, sex_combined: true, sex_male: false, sex_female: false, male_count: '', female_count: '' }
          : { ...prev, sex_combined: false, combined_count: '' };
      }
      return {
        ...prev,
        [`sex_${key}`]: checked,
        ...(checked ? { sex_combined: false, combined_count: '' } : { [`${key}_count`]: '' }),
      };
    });
  };

  const renderSexCountsField = (field) => renderFieldShell(
    {
      ...field,
      label: 'Sex & Population Count',
      hint: 'Tick Male and/or Female and enter each count — one record is saved per sex. Tick Combined for a single count covering both sexes.',
    },
    <div className="space-y-3">
      <div className="flex flex-wrap gap-5 pt-1">
        {SEX_CHOICES.map((opt) => (
          <label key={opt.key} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              checked={Boolean(popForm[`sex_${opt.key}`])}
              onChange={toggleSex(opt.key)}
              disabled={fieldsLocked}
              className="h-4 w-4 rounded accent-green-600"
            />
            {opt.label}
          </label>
        ))}
      </div>
      {(popForm.sex_male || popForm.sex_female || popForm.sex_combined) && (
        <div className="grid grid-cols-2 gap-4">
          {popForm.sex_male && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Male Count</label>
              <input type="number" min="1" value={popForm.male_count} onChange={setPop('male_count')} placeholder="0" className={INP} disabled={fieldsLocked} />
            </div>
          )}
          {popForm.sex_female && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Female Count</label>
              <input type="number" min="1" value={popForm.female_count} onChange={setPop('female_count')} placeholder="0" className={INP} disabled={fieldsLocked} />
            </div>
          )}
          {popForm.sex_combined && (
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Combined Count (male and female together)</label>
              <input type="number" min="1" value={popForm.combined_count} onChange={setPop('combined_count')} placeholder="0" className={INP} disabled={fieldsLocked} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderGeospatialSection = (section) => {
    const geospatialField = getSchemaFields(section?.id)?.[0] ?? section?.fields?.[0] ?? { label: section?.title ?? 'Geospatial', required: true };

    return (
      <div className="space-y-4">
        <SectionHead title={section?.title || 'Geospatial'} />
        <div className="grid grid-cols-3 gap-4">
          <Field label="State" required>
            {(isR04 || isR05) ? (
              <input className={INP_LOCKED} value={popForm.state} readOnly />
            ) : (
              <Select value={popForm.state} onChange={setPop('state')} disabled={fieldsLocked}>
                <option value="">Select...</option>
                {states.map((state) => <option key={state} value={state}>{state}</option>)}
              </Select>
            )}
          </Field>
          <Field label="LGA" required>
            {isR05 ? (
              <input className={INP_LOCKED} value={popForm.lga} readOnly />
            ) : (
              <Select value={popForm.lga} onChange={setPop('lga')} disabled={fieldsLocked || !popForm.state || loadingLgas}>
                <option value="">{!popForm.state ? 'Select state first' : loadingLgas ? 'Loading...' : 'LGA...'}</option>
                {lgasList.map((lga) => <option key={lga} value={lga}>{lga}</option>)}
              </Select>
            )}
          </Field>
          <Field label="Ward">
            <Select value={popForm.ward} onChange={setPop('ward')} disabled={fieldsLocked || !popForm.lga || loadingWards}>
              <option value="">{!popForm.lga ? 'Select LGA first' : loadingWards ? 'Loading...' : 'Ward...'}</option>
              {wardsList.map((ward) => <option key={ward} value={ward}>{ward}</option>)}
            </Select>
          </Field>
        </div>
        {geospatialField?.hint && <p className="text-xs text-gray-400">{geospatialField.hint}</p>}
      </div>
    );
  };

  // ── Step content: Basic Data ────────────────────────────────────────────
  // Step content: Basic Data
  // eslint-disable-next-line no-unused-vars
  function renderBasicData() {
    const referencePeriodSection = getSchemaSection('reference-period', 'Reference Period');
    const referencePeriodFields = getSchemaFields('reference-period');

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title="Select Module" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Module" required>
              <ReadonlyBox>{moduleScopeLabel}</ReadonlyBox>
            </Field>
            <Field label="Sub Module" required>
              <ReadonlyBox>{subModuleScopeLabel}</ReadonlyBox>
            </Field>
          </div>
        </div>

        <SectionDivider />

        {renderGeospatialSection(getSchemaSection('geospatial', 'Geospatial'))}

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title={referencePeriodSection.title || 'Reference Period'} />
          <div className="grid grid-cols-2 gap-4">
            {referencePeriodFields.length > 0 ? referencePeriodFields.map((field) => {
              if (field.id === 'period-type') {
                return renderSelectField(
                  field,
                  popForm.period_type,
                  setPop('period_type'),
                  getFieldOptions(field),
                  { disabled: fieldsLocked }
                );
              }

              if (field.id === 'period-instance') {
                const options = periodInstances(popForm.period_type);
                return renderSelectField(
                  field,
                  popForm.period_instance,
                  setPop('period_instance'),
                  normalizeInlineOptions(options),
                  { disabled: fieldsLocked || !popForm.period_type, placeholder: !popForm.period_type ? 'Select type first' : (field.placeholder || 'Select...') }
                );
              }

              return null;
            }) : (
              <>
                {renderSelectField(
                  { id: 'period-type', label: 'Period Type', required: true, placeholder: 'Select...' },
                  popForm.period_type,
                  setPop('period_type'),
                  normalizeInlineOptions(['Baseline', 'Monthly', 'Quarterly', 'Annual']),
                  { disabled: fieldsLocked }
                )}
                {renderSelectField(
                  { id: 'period-instance', label: 'Period Instance', required: true, placeholder: 'Select...' },
                  popForm.period_instance,
                  setPop('period_instance'),
                  normalizeInlineOptions(periodInstances(popForm.period_type)),
                  { disabled: fieldsLocked || !popForm.period_type, placeholder: !popForm.period_type ? 'Select type first' : 'Select...' }
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Step content: Animal Information Data ───────────────────────────────
  // eslint-disable-next-line no-unused-vars
  function renderAnimalInfo() {
    if (subModule === 'population') {
      return (
        <div className="space-y-5">
          <div className="space-y-4">
            <SectionHead title="Animal Data" />
            <Field label="Livestock Species" required>
              <Select value={popForm.species_uuid} onChange={setPop('species_uuid')} disabled={fieldsLocked}>
                <option value="">Select…</option>
                {species.map((s) => <option key={s.uuid} value={s.uuid}>{s.common_name_nigeria || s.species_code}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Production System" required>
                <Select value={popForm.production_system} onChange={setPop('production_system')} disabled={fieldsLocked}>
                  <option value="">Select…</option>
                  {productionSystems.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Select>
              </Field>
              <Field label="Production Purpose" required>
                <Select value={popForm.production_purpose} onChange={setPop('production_purpose')} disabled={fieldsLocked || !popForm.species_uuid}>
                  <option value="">{!popForm.species_uuid ? 'Select species first' : 'Select…'}</option>
                  {purposes.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Population Count" required hint="Total animals, colonies for bees. Must be > 0">
              <input type="number" min="1" value={popForm.population_count} onChange={setPop('population_count')} placeholder="0" className={INP} disabled={fieldsLocked} />
            </Field>
          </div>

          <SectionDivider />

          <div className="space-y-4">
            <SectionHead title="Classification" />
            <Field label="Sex" required>
              <div className="flex gap-5 pt-1">
                {['male', 'female', 'combined'].map((s) => (
                  <label key={s} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                    <input type="radio" name="pop_sex" value={s} checked={popForm.sex === s} onChange={setPop('sex')} disabled={fieldsLocked} className="accent-green-600" />
                    {lbl(s)}
                  </label>
                ))}
              </div>
            </Field>

            {!hideRestrictedFields && (
              <Field label="Age Group" required>
                <Select value={popForm.age_class} onChange={setPop('age_class')} disabled={fieldsLocked}>
                  <option value="">Select…</option>
                  {ageClassOpts.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </Select>
              </Field>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field label="Number of Herds / Flocks" required hint="≥ 1">
                <input type="number" min="1" value={popForm.number_herds_flocks} onChange={setPop('number_herds_flocks')} placeholder="0" className={INP} disabled={fieldsLocked} />
              </Field>
              <Field label="Period Offtake" required hint="Sold / slaughtered this period — enter 0 if none">
                <input type="number" min="0" value={popForm.period_offtake} onChange={setPop('period_offtake')} placeholder="0" className={INP} disabled={fieldsLocked} />
              </Field>
            </div>
          </div>
        </div>
      );
    }
  }

  // ── Step content: System Info ───────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  function renderSystemInfo() {
    if (subModule === 'population') {
      return (
        <div className="space-y-5">
          <div className="space-y-4">
            <SectionHead title="Temporal / Source" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Count Method" required>
                <Select value={popForm.count_method} onChange={setPop('count_method')} disabled={fieldsLocked}>
                  <option value="">Select…</option>
                  {countMethods.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Select>
              </Field>
              <SelectWithOther
                label="Data Source"
                required
                value={popForm.data_source}
                otherValue={popForm.data_source_other}
                onChange={setPop('data_source')}
                onOtherChange={(value) => setPopForm((p) => ({ ...p, data_source_other: value }))}
                options={dataSources}
                placeholder="Select..."
                otherPlaceholder="Describe the source"
                disabled={fieldsLocked}
              />
            </div>
          </div>

          {!hideRestrictedFields && (
            <>
              <SectionDivider />
              <div className="space-y-4">
                <SectionHead title="Registry" />
                <Field label="Breed / Ecotype" hint="Optional — select from registry">
                  <Select value={popForm.breed_uuid} onChange={setPop('breed_uuid')} disabled={fieldsLocked || !popForm.species_uuid || loadingBreeds}>
                    <option value="">{!popForm.species_uuid ? 'Select species first' : loadingBreeds ? 'Loading…' : 'Select from registry…'}</option>
                    {breedList.map((b) => <option key={b.uuid} value={b.uuid}>{b.breed_name || b.breed_code}</option>)}
                  </Select>
                </Field>
              </div>
            </>
          )}

          <SectionDivider />

          <div className="space-y-4">
            <SectionHead title="System" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Confidence Level" hint="Auto-derived from Count Method">
                <ReadonlyBox>
                  {isEditMode && editRow?.confidenceLevel
                    ? editRow.confidenceLevel
                    : `${deriveConfidenceLevel(popForm.count_method)} (preview, from Count Method)`}
                </ReadonlyBox>
              </Field>
              <Field label="Record Status">
                <ReadonlyBox>{isEditMode ? `${lbl(currentStatus)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
              </Field>
            </div>
          </div>

          {isEditMode && !isR05 && (
            <>
              <SectionDivider />
              <div className="space-y-4">
                <SectionHead title="Notes" />
                <Field label="Notes">
                                    <textarea
                    value={popForm.notes}
                    onChange={setPop('notes')}
                    rows={2}
                    placeholder="Optional notes…"
                    disabled={fieldsLocked}
                    className="w-full rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white resize-none placeholder-gray-400 disabled:cursor-not-allowed disabled:text-gray-400"
                  ></textarea>
                </Field>
              </div>
            </>
          )}

          {isEditMode && !isFieldUser && (() => {
            const nextStates = (ROLE_TRANSITIONS[roleId] ?? {})[currentStatus] ?? [];
            if (nextStates.length === 0) return null;
            const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
            const allOptions = [{ value: currentStatus, label: cap(currentStatus) }, ...nextStates.map((s) => ({ value: s, label: cap(s) }))];
            return (
              <>
                <SectionDivider />
                <div className="space-y-4">
                  <SectionHead title="Record Status" />
                  <Field label="Validation Status" hint="Select the next pipeline stage, then save on the Review step">
                    <Select
                      value={statusSelect}
                      onChange={(e) => { setStatusSelect(e.target.value); setRejectionNotes(''); }}
                    >
                      {allOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </Field>
                  {statusSelect === 'rejected' && (
                    <Field label="Rejection Reason" required hint="Required — will be shown to the originating officer">
                                            <textarea
                        value={rejectionNotes}
                        onChange={(e) => setRejectionNotes(e.target.value)}
                        rows={3}
                        placeholder="Describe the issue(s) that must be corrected before resubmitting…"
                        className="w-full rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-red-400 resize-none placeholder-gray-400"
                      ></textarea>
                      {rejectionNotes.trim().length === 0 && (
                        <p className="mt-1 text-xs text-red-600">Rejection reason is required.</p>
                      )}
                    </Field>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      );
    }

  }

  // ── Step content: Review ────────────────────────────────────────────────
  function ReviewRow({ label: labelText, value }) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
        <span className="text-xs font-semibold text-gray-500">{labelText}</span>
        <span className="text-sm text-gray-900 text-right">{value || value === 0 ? String(value) : '—'}</span>
      </div>
    );
  }

  function renderReview() {
    if (subModule === 'population') {
      const speciesLabelText = species.find((s) => s.uuid === popForm.species_uuid)?.common_name_nigeria
        || popForm.species_code || '—';
      const breedLabelText = breedList.find((b) => b.uuid === popForm.breed_uuid)?.breed_name || null;
      return (
        <>
          {isEditMode && <ReviewRow label="Current Status" value={lbl(currentStatus)} />}
          <SectionHead title="Geospatial & Period" />
          <ReviewRow label="State" value={popForm.state} />
          <ReviewRow label="LGA" value={popForm.lga} />
          <ReviewRow label="Ward" value={popForm.ward} />
          <ReviewRow label="Reference Period" value={popForm.period_instance} />

          <SectionHead title="Animal Data" />
          <ReviewRow label="Species" value={speciesLabelText} />
          <ReviewRow label="Production System" value={lbl(popForm.production_system)} />
          <ReviewRow label="Production Purpose" value={getProductionPurposeLabel(popForm.production_purpose, purposes)} />
          {(
            <>
              <ReviewRow label="Population Count" value={formatCount(sexTotal)} />
              <ReviewRow
                label="Sex"
                value={sexRows.map((row) => lbl(row.sex)).join(' / ')}
              />
              {/* The records exactly as they will appear in the Livestock
                  Population table — one row per sex (C1 long-format). */}
              <div className="py-2 border-b border-gray-50">
                <span className="text-xs font-semibold text-gray-500">
                  {isEditMode ? 'Records in this entry' : 'Records to be created'} ({sexRows.length})
                </span>
                <table className="mt-2 w-full overflow-hidden rounded-lg border border-gray-100 text-sm">
                  <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Sex</th>
                      <th className="px-3 py-2 text-right">Population</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sexRows.map((row) => (
                      <tr key={row.sex}>
                        <td className="px-3 py-2 text-gray-900">{lbl(row.sex)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-900">{formatCount(row.population_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!hideRestrictedFields && <ReviewRow label="Age Group" value={ageClassOpts.find((a) => a.value === popForm.age_class)?.label} />}
          <ReviewRow label="Number of Herds / Flocks" value={popForm.number_herds_flocks} />
          <ReviewRow label="Period Offtake" value={popForm.period_offtake} />

          <SectionHead title="System" />
          <ReviewRow label="Count Method" value={countMethods.find((m) => m.value === popForm.count_method)?.label} />
          <ReviewRow
            label="Data Source"
            value={formatOtherOptionValue(popForm.data_source, popForm.data_source_other, dataSources)}
          />
          {!hideRestrictedFields && <ReviewRow label="Breed / Ecotype" value={breedLabelText} />}
          {isEditMode && !isR05 && <ReviewRow label="Notes" value={popForm.notes} />}
          {isEditMode && !isFieldUser && statusSelect !== currentStatus && (
            <ReviewRow label="New Status" value={lbl(statusSelect)} />
          )}
        </>
      );
    }

  }

  // ── Review-step action buttons (role + sub-module aware) ────────────────
  function renderReviewFooter() {
    if (isEditMode) {
      return (
        <>
          <button type="button" onClick={() => setStep((s) => s - 1)}
            className="flex-1 min-w-[100px] h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Previous
          </button>
          {isFieldUser ? (
            <>
              <button type="button" onClick={() => handleEditSave({ submitAfterSave: false })} disabled={!canSubmitEdit || saving}
                className="flex-1 min-w-[130px] h-11 rounded-full border border-gray-300 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                {saving ? 'Saving…' : (currentStatus === 'rejected' ? 'Save Changes' : 'Save as Draft')}
              </button>
              <button type="button" onClick={() => handleEditSave({ submitAfterSave: true })} disabled={!canSubmitEdit || saving}
                className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {saving ? 'Submitting…' : 'Submit for Review'}
              </button>
            </>
          ) : (
            <button type="button" onClick={() => handleEditSave({})} disabled={!canSubmitEdit || saving}
              className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? (fieldsLocked ? 'Applying…' : 'Saving…') : (fieldsLocked ? 'Apply Status' : 'Save Changes')}
            </button>
          )}
        </>
      );
    }


    return (
      <>
        <button type="button" onClick={() => setStep((s) => s - 1)}
          className="flex-1 min-w-[100px] h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
          Previous
        </button>
        {isR05 ? (
          <>
            <button type="button" onClick={handlePopSaveDraft} disabled={saving}
              className="flex-1 min-w-[130px] h-11 rounded-full border border-gray-300 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save as Draft'}
            </button>
            <button type="button" onClick={() => setShowSubmitConfirm(true)} disabled={savedRecords.length === 0 || submitting}
              className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? 'Submitting…' : 'Submit for Review'}
            </button>
          </>
        ) : (
          <button type="button" onClick={handlePopCreate} disabled={saving}
            className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Creating…' : 'Create'}
          </button>
        )}
      </>
    );
  }

  function renderBasicDataRegistry() {
    const referencePeriodSection = getSchemaSection('reference-period', 'Reference Period');
    const referencePeriodFields = getSchemaFields('reference-period');

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title="Select Module" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Module" required>
              <ReadonlyBox>{moduleScopeLabel}</ReadonlyBox>
            </Field>
            <Field label="Sub Module" required>
              <ReadonlyBox>{subModuleScopeLabel}</ReadonlyBox>
            </Field>
          </div>
        </div>

        <SectionDivider />

        {renderGeospatialSection(getSchemaSection('geospatial', 'Geospatial'))}

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title={referencePeriodSection.title || 'Reference Period'} />
          <div className="grid grid-cols-2 gap-4">
            {(referencePeriodFields.length > 0 ? referencePeriodFields : [
              { id: 'period-type', label: 'Period Type', required: true, placeholder: 'Select...' },
              { id: 'period-instance', label: 'Period Instance', required: true, placeholder: 'Select...' },
            ]).map((field) => {
              if (field.id === 'period-type') {
                return renderSelectField(
                  field,
                  popForm.period_type,
                  setPop('period_type'),
                  getFieldOptions(field),
                  { disabled: fieldsLocked }
                );
              }

              if (field.id === 'period-instance') {
                const options = periodInstances(popForm.period_type);
                return renderSelectField(
                  field,
                  popForm.period_instance,
                  setPop('period_instance'),
                  normalizeInlineOptions(options),
                  {
                    disabled: fieldsLocked || !popForm.period_type,
                    placeholder: !popForm.period_type ? 'Select type first' : (field.placeholder || 'Select...'),
                  }
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>
    );
  }

  function renderAnimalInfoRegistry() {
    const animalDataSection = getSchemaSection('animal-data', 'Animal Data');
    const classificationSection = getSchemaSection('classification', 'Classification');
    const animalFields = getSchemaFields('animal-data');
    const classificationFields = getSchemaFields('classification');

    const animalFieldList = animalFields.length > 0 ? animalFields : [
      { id: 'livestock-species', label: 'Livestock Species', required: true, layout: 'full-width', placeholder: 'Select...' },
      { id: 'production-system', label: 'Production System', required: true, placeholder: 'Select...' },
      { id: 'production-purpose', label: 'Production Purpose', required: true, placeholder: 'Select species first' },
      { id: 'population-count', label: 'Population Count', required: true, layout: 'full-width', placeholder: '0', hint: 'Total animals, colonies for bees. Must be > 0.' },
    ];

    const classificationFieldList = classificationFields.length > 0 ? classificationFields : [
      { id: 'sex', type: 'checkbox', label: 'Sex', required: true, layout: 'full-width', options: ['Male', 'Female', 'Combined'], exclusiveOptions: ['Combined'] },
      { id: 'age-group', label: 'Age Group', required: true, layout: 'full-width', placeholder: 'Select...' },
      { id: 'number-of-herds-flocks', label: 'Number of Herds / Flocks', required: true, placeholder: '0', hint: '>= 1' },
      { id: 'period-offtake', label: 'Period Offtake', required: true, placeholder: '0', hint: 'Sold or slaughtered this period - enter 0 if none.' },
    ];

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title={animalDataSection.title || 'Animal Data'} />
          <div className="grid grid-cols-2 gap-4">
            {animalFieldList.map((field) => {
              switch (field.id) {
                case 'livestock-species':
                  return renderSelectField(field, popForm.species_uuid, setPop('species_uuid'), getFieldOptions(field), { disabled: fieldsLocked });
                case 'production-system':
                  return renderSelectField(field, popForm.production_system, setPop('production_system'), getFieldOptions(field), { disabled: fieldsLocked });
                case 'production-purpose':
                  return renderSelectField(field, popForm.production_purpose, setPop('production_purpose'), getFieldOptions(field), {
                    disabled: fieldsLocked || !popForm.species_uuid,
                    placeholder: !popForm.species_uuid ? 'Select species first' : (field.placeholder || 'Select...'),
                  });
                case 'population-count':
                  // Counts are entered per sex under Classification (create and
                  // edit alike), so this shows their total.
                  return renderFieldShell(
                    { ...field, hint: 'Total of the counts entered per sex below.' },
                    <ReadonlyBox>{sexTotal > 0 ? formatCount(sexTotal) : 'Enter counts under Sex below'}</ReadonlyBox>
                  );
                default:
                  return null;
              }
            })}
          </div>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title={classificationSection.title || 'Classification'} />
          <div className="grid grid-cols-2 gap-4">
            {classificationFieldList.map((field) => {
              if (field.id === 'sex') {
                // sex is ENUM male / female / combined, captured the Create New
                // way in both create and edit (one record per sex).
                return renderSexCountsField(field);
              }

              if (field.id === 'age-group') {
                if (hideRestrictedFields) return null;
                return renderSelectField(field, popForm.age_class, setPop('age_class'), getFieldOptions(field), { disabled: fieldsLocked });
              }

              if (field.id === 'number-of-herds-flocks') {
                return renderInputField(field, popForm.number_herds_flocks, setPop('number_herds_flocks'), {
                  disabled: fieldsLocked,
                  type: 'number',
                  min: 1,
                });
              }

              if (field.id === 'period-offtake') {
                return renderInputField(field, popForm.period_offtake, setPop('period_offtake'), {
                  disabled: fieldsLocked,
                  type: 'number',
                  min: 0,
                });
              }

              return null;
            })}
          </div>
        </div>
      </div>
    );
  }

  function renderSystemInfoRegistry() {
    const temporalSourceSection = getSchemaSection('temporal-source', 'Temporal / Source');
    const registrySection = getSchemaSection('registry', 'Registry');
    const temporalSourceFields = getSchemaFields('temporal-source');
    const registryFields = getSchemaFields('registry');

    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title={temporalSourceSection.title || 'Temporal / Source'} />
          <div className="grid grid-cols-2 gap-4">
            {(temporalSourceFields.length > 0 ? temporalSourceFields : [
              { id: 'count-method', label: 'Count Method', required: true, placeholder: 'Select...' },
              { id: 'data-source', label: 'Data Source', required: true, placeholder: 'Select...' },
            ]).map((field) => {
              if (field.id === 'count-method') {
                return renderSelectField(field, popForm.count_method, setPop('count_method'), getFieldOptions(field), { disabled: fieldsLocked });
              }

              if (field.id === 'data-source') {
                return (
                  <div className={field?.layout === 'full-width' ? 'col-span-2' : 'col-span-1'}>
                    <SelectWithOther
                      label={field.label}
                      required={field.required}
                      value={popForm.data_source}
                      otherValue={popForm.data_source_other}
                      onChange={setPop('data_source')}
                      onOtherChange={(value) => setPopForm((p) => ({ ...p, data_source_other: value }))}
                      options={getFieldOptions(field)}
                      placeholder={field.placeholder || 'Select...'}
                      otherPlaceholder={field.otherPlaceholder || 'Describe the source'}
                      disabled={fieldsLocked}
                    />
                  </div>
                );
              }

              return null;
            })}
          </div>
        </div>

        {!hideRestrictedFields && (
          <>
            <SectionDivider />
            <div className="space-y-4">
              <SectionHead title={registrySection.title || 'Registry'} />
              <div className="grid grid-cols-2 gap-4">
                {(registryFields.length > 0 ? registryFields : [
                  { id: 'breed-ecotype', label: 'Breed / Ecotype', layout: 'full-width', placeholder: 'Select from registry' },
                ]).map((field) => {
                  if (field.id !== 'breed-ecotype') return null;
                  return renderSelectField(field, popForm.breed_uuid, setPop('breed_uuid'), getFieldOptions(field), {
                    disabled: fieldsLocked || !popForm.species_uuid || loadingBreeds,
                    placeholder: !popForm.species_uuid ? 'Select species first' : (loadingBreeds ? 'Loading...' : (field.placeholder || 'Select from registry...')),
                  });
                })}
              </div>
            </div>
          </>
        )}

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Confidence Level" hint="Auto-derived from Count Method">
              <ReadonlyBox>
                {isEditMode && editRow?.confidenceLevel
                  ? editRow.confidenceLevel
                  : `${deriveConfidenceLevel(popForm.count_method)} (preview, from Count Method)`}
              </ReadonlyBox>
            </Field>
            <Field label="Record Status">
              <ReadonlyBox>{isEditMode ? `${lbl(currentStatus)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
            </Field>
          </div>
        </div>

        {isEditMode && !isR05 && (
          <>
            <SectionDivider />
            <div className="space-y-4">
              <SectionHead title="Notes" />
              {renderTextareaField(
                { id: 'notes', label: 'Notes', placeholder: 'Optional notes...' },
                popForm.notes,
                setPop('notes'),
                { disabled: fieldsLocked }
              )}
            </div>
          </>
        )}

        {isEditMode && !isFieldUser && (() => {
          const nextStates = (ROLE_TRANSITIONS[roleId] ?? {})[currentStatus] ?? [];
          if (nextStates.length === 0) return null;
          const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
          const allOptions = [{ value: currentStatus, label: cap(currentStatus) }, ...nextStates.map((s) => ({ value: s, label: cap(s) }))];
          return (
            <>
              <SectionDivider />
              <div className="space-y-4">
                <SectionHead title="Record Status" />
                <Field label="Validation Status" hint="Select the next pipeline stage, then save on the Review step">
                  <Select
                    value={statusSelect}
                    onChange={(e) => { setStatusSelect(e.target.value); setRejectionNotes(''); }}
                  >
                    {allOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </Field>
                {statusSelect === 'rejected' && (
                  <Field label="Rejection Reason" required hint="Required - will be shown to the originating officer">
                    <textarea
                      value={rejectionNotes}
                      onChange={(e) => setRejectionNotes(e.target.value)}
                      rows={3}
                      placeholder="Describe the issue(s) that must be corrected before resubmitting..."
                      className="w-full rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-red-400 resize-none placeholder-gray-400"
                    />
                    {rejectionNotes.trim().length === 0 && (
                      <p className="mt-1 text-xs text-red-600">Rejection reason is required.</p>
                    )}
                  </Field>
                )}
              </div>
            </>
          );
        })()}
      </div>
    );
  }

  const stepRenderers = [renderBasicDataRegistry, renderAnimalInfoRegistry, renderSystemInfoRegistry, renderReview];

  // ── View-only mode: Review summary + an Edit Record button only for viewers
  // who are actually allowed to edit this record; everyone else just sees
  // the read-only Review information. ────────────────────────────────────
  if (viewOnly) {
    return (
      <div className="p-6 bg-white min-h-screen">
        {loadingRecord && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
            Loading record…
          </div>
        )}
        {error && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
              <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-500">Status:</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize bg-gray-100 text-gray-600">
                {lbl(currentStatus)}
              </span>
            </div>
          </div>

          {/* R06 is a read-only viewer restricted to approved records — defend
              this page the same way the table listing already scopes their
              view, in case this record's status changed after the list loaded. */}
          {roleId === 'R06' && editRow && currentStatus !== 'approved' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              This record is not yet Approved — your role can only view Approved records.
            </div>
          ) : (
            <div className="space-y-4">{renderReview()}</div>
          )}

          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={goToTab}
              className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Back
            </button>
            {canEditThisRecord && (
              <button
                type="button"
                onClick={() => navigate(`/livestock-population/edit/${uuid}`, { state: { row: editRow } })}
                className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700"
              >
                Edit Record
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      {refLoading && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          Loading foundation references…
        </div>
      )}

      {isEditMode && loadingRecord && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          Loading record…
        </div>
      )}

      {isEditMode && currentStatus === 'rejected' && editRow?.rejection_notes && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-red-700">⚠ Rejection Reason</p>
          <p className="text-sm text-red-800">{editRow.rejection_notes}</p>
          <p className="mt-1.5 text-xs font-medium text-red-600">Please address the above before resubmitting.</p>
        </div>
      )}

      {fieldsLocked && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This record is <strong>{lbl(currentStatus)}</strong> — field values are locked. Only the status transition on the Review step will be applied when you save.
        </div>
      )}

      <RegistryCustomFields moduleKey={MODULE_KEY} submoduleKey={subModule} />

      <RecordWizard
        steps={WIZARD_STEPS}
        currentStep={step}
        title={isEditMode ? 'Edit Record' : 'Add New Record'}
        subtitle={isEditMode ? 'Update the details for this record' : 'Fill in the details to create a new record'}
        statusLabel={statusLabel}
        error={error || successMsg}
        onCancel={goToTab}
        onBack={() => setStep((s) => s - 1)}
        onNext={() => setStep((s) => s + 1)}
        canGoNext={stepValid}
        nextLabel="Next"
        footer={step === 3 ? <>{renderReviewFooter()}</> : undefined}
      >
        {stepRenderers[step]()}
      </RecordWizard>

      {showSubmitConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-2 text-base font-bold text-gray-900">Submit for Review?</h3>
            <p className="mb-6 text-sm text-gray-600">
              Once submitted, this record will be locked pending review by the State Data Officer.
              You will no longer be able to edit it.
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 h-10 rounded-full border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={handlePopSubmitForReview}
                className="flex-1 h-10 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
